import { NgClass } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { ArbetsorderInfoIconComponent } from "@components/arbetsorder-info-icon/arbetsorder-info-icon.component";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { DivListComponent } from "@components/list/div-list/div-list.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { SelectorLabelBindingDirective } from "@components/selector/selector-label-binding.directive";
import { SelectorComponent } from "@components/selector/selector.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { sortBy } from "lodash";
import moment from "moment";
import { Subject, from } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ListComponent } from "~app/common/components/list/list.component";
import { ToastService } from "~app/common/components/toast/toast.service";
import { Maskinlagstyp } from "~app/common/models/constants";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager, ICommandManager } from "~app/common/services/cqs/commandManager";
import { MaskinlagService } from "~app/common/services/maskinlag.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IProduktionsmall } from "~app/features/planera/arbetsorder-produktval/arbetsorder-produktval.models";
import { ArbetsorderProduktvalService } from "~app/features/planera/arbetsorder-produktval/arbetsorder-produktval.service";
import { SetMaskinlagCommand } from "~app/features/planera/commands/SetMaskinlagCommand";
import { SetPeriodsattPeriodCommand } from "~app/features/planera/commands/SetPeriodsattPeriodCommand";
import { IMaskinlag } from "~app/features/planera/model/IMaskinLag";
import { ArbetsorderService } from "~app/features/planera/services/arbetsorder.service";
import { ApplyProduktionsMallAndUtbytessimuleraEgenskogCommand } from "../../../commands/ApplyProduktionsMallAndUtbytessimuleraEgenskogCommand";
import { UtbytessimuleraFromVirkeskopskontraktWithProduktionsmallCommand } from "../../../commands/UtbytessimuleraFromVirkeskopskontraktWithProduktionsmallCommand";
import { IIntentTypeItem, IntentType, MasshanteringArbetsorder, MasshanteringArbetsorderViewModel } from "../arbetsorder-masshantering.models";

@Component({
    selector: "rask-arbetsorder-masshantering-dialog",
    templateUrl: "./arbetsorder-masshantering-dialog.component.html",
    styleUrls: ["./arbetsorder-masshantering-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectorComponent,
        FormsModule,
        DatepickerComponent,
        SelectorLabelBindingDirective,
        RouterLink,
        DivListComponent,
        ListColumnDirective,
        ArbetsorderInfoIconComponent,
        NgClass,
        ButtonComponent
    ]
})

export class ArbetsorderMasshanteringDialogComponent implements OnInit, OnDestroy {
    @ViewChild("arbetsorderList") arbetsorderListInstance: ListComponent;
    data: MasshanteringArbetsorderViewModel;
    intent: IntentType;
    intents: IIntentTypeItem[] = [
        {
            key: IntentType.periodsatt,
            value: "Periodsätt"
        },
        {
            key: IntentType.schemalagg,
            value: "Schemalägg"
        },
        {
            key: IntentType.avplanera,
            value: "Avplanera"
        },
        {
            key: IntentType.setMaskinlag,
            value: "Tilldela maskinlag"
        }
    ];
    minaMaskinlag: IMaskinlag[] = [];
    modal = inject(NgbActiveModal);
    periodsattPeriod: string;
    periodsattPeriodMinDate: string;

    selectecMaskinLag: IMaskinlag;
    produktionsmallar: IProduktionsmall[];
    selectedProduktionsmall: IProduktionsmall;

    private readonly arbetsorderProduktvalService = inject(ArbetsorderProduktvalService);
    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly busyService = inject(BusyService);
    private readonly commandManager: ICommandManager;
    private readonly commandService = inject(CommandService);
    private readonly maskinlagService = inject(MaskinlagService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly toastService = inject(ToastService);

    private allaMaskinlag: IMaskinlag[] = [];

    constructor() {
        this.periodsattPeriodMinDate = DateUtils.getStartOfMonth();
        this.arbetsorderProduktvalService.produktionsMallar$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(x => {
                this.produktionsmallar = x;
            });
        this.arbetsorderProduktvalService.refreshproduktionsMallar();
        this.commandManager = new CommandManager();
    }

    get isMaskinlagDisabled(): boolean {
        return this.intent === IntentType.avplanera;
    }

    get isTidssattningDisabled(): boolean {
        return this.intent === IntentType.avplanera || this.intent === IntentType.setMaskinlag;
    }

    intentSelectedFormatter = (item: IIntentTypeItem) => ObjectUtils.hasValue(item?.key) ? item.key : item;

    intentViewFormatter = (item: IIntentTypeItem) => item?.value;

    maskinlagSelectionFormatter = (item: IMaskinlag) => item?.id;

    maskinlagViewFormatter = (result: IMaskinlag) => result ? `${result.csrNummer} ${result.namn}` : null;

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.loadMaskinlag();

        this.sortMasshanteringArbetsorderList(this.data.masshanteringArbetsorder);
    }

    protected canSave(): boolean {
        if (!ObjectUtils.isNullOrUndefined(this.selectedProduktionsmall)) {
            return true;
        }
        switch (this.intent) {
            case IntentType.avplanera:
                return true;
            case IntentType.periodsatt:
                return ObjectUtils.hasValue(this.periodsattPeriod);
            case IntentType.schemalagg:
                return ObjectUtils.hasValue(this.periodsattPeriod) &&
                    this.data.masshanteringArbetsorder.some(x => ObjectUtils.hasValue(x.nyttMaskinlagId));
            case IntentType.setMaskinlag:
                return this.data.masshanteringArbetsorder.some(x => ObjectUtils.hasValue(x.nyttMaskinlagId));
            default:
                return false;
        }
    }

    protected dismiss(): void {
        this.modal.close();
    }

    protected getArbetsorderPeriod(arbetsorder: MasshanteringArbetsorder): string {
        if (!arbetsorder) {
            return "";
        }

        return this.arbetsorderService.getArbetsorderOperationPeriod(arbetsorder.ref);
    }

    protected getMaskinlagName(maskinlagId?: number): string {
        if (ObjectUtils.isNullOrUndefined(maskinlagId)) {
            return "";
        }

        const maskinlag = this.allaMaskinlag.find(x => x.id === maskinlagId);
        return maskinlag ? `${maskinlag.csrNummer} - ${maskinlag.namn}` : `${maskinlagId}`;
    }

    protected onIntentChanged($event: IntentType): void {
        this.data.masshanteringArbetsorder.forEach(x => {
            x.intent = $event;
        });
    }

    protected onMaskinlagChanged(): void {
        this.data.masshanteringArbetsorder.forEach(element => {
            if (!element.isLocked && ObjectUtils.hasValue(this.selectecMaskinLag?.id) && element.maskinlag.some(x => x.id === this.selectecMaskinLag?.id)) {
                element.nyttMaskinlagId = this.selectecMaskinLag?.id;
            } else {
                element.nyttMaskinlagId = undefined;
            }
        });
    }

    protected async onProduktionsmallChanged(mall: IProduktionsmall): Promise<void> {
        if (mall === null || mall === undefined) {
            return;
        }
        this.arbetsorderListInstance.getSortedData()
            .filter((x: MasshanteringArbetsorder) => x.applicable)
            .forEach(x => {
                if (x.isVirkeskop) {
                    this.commandManager.addOrUpdate(new UtbytessimuleraFromVirkeskopskontraktWithProduktionsmallCommand((instance) => {
                        instance.arbetsorderId = x.id;
                        instance.produktionsmallId = mall.id;
                    }), c => c.$type === UtbytessimuleraFromVirkeskopskontraktWithProduktionsmallCommand.$type && c.arbetsorderId === x.id);
                } else {
                    this.commandManager.addOrUpdate(new ApplyProduktionsMallAndUtbytessimuleraEgenskogCommand((instance) => {
                        instance.arbetsorderId = x.id;
                        instance.produktionsmallId = mall.id;
                        instance.utbytessimulera = true;
                    }), c => c.$type === ApplyProduktionsMallAndUtbytessimuleraEgenskogCommand.$type && c.arbetsorderId === x.id);
                }
            });
    }

    protected save(): void {
        this.arbetsorderListInstance.getSortedData()
            .filter((x: MasshanteringArbetsorder) => x.applicable)
            .forEach((x: MasshanteringArbetsorder) => {
                switch (this.intent) {
                    case IntentType.avplanera:
                        this.commandManager.addOrUpdate(new SetMaskinlagCommand((instance) => {
                            instance.arbetsorderId = x.id;
                        }), c => c.$type === SetMaskinlagCommand.$type && c.arbetsorderId === x.id);
                        this.commandManager.addOrUpdate(new SetPeriodsattPeriodCommand((instance) => {
                            instance.arbetsorderId = x.id;
                        }), c => c.$type === SetPeriodsattPeriodCommand.$type && c.arbetsorderId === x.id);
                        break;
                    case IntentType.periodsatt:
                        this.commandManager.addOrUpdate(new SetPeriodsattPeriodCommand((instance) => {
                            instance.arbetsorderId = x.id;
                            instance.periodsattPeriod = this.periodsattPeriod;
                        }), c => c.$type === SetPeriodsattPeriodCommand.$type && c.arbetsorderId === x.id);
                        if (ObjectUtils.hasValue(x.nyttMaskinlagId)) {
                            this.commandManager.addOrUpdate(new SetMaskinlagCommand((instance) => {
                                instance.arbetsorderId = x.id;
                                instance.maskinlagId = x.nyttMaskinlagId;
                            }), c => c.$type === SetMaskinlagCommand.$type && c.arbetsorderId === x.id);
                        }
                        break;
                    case IntentType.schemalagg:
                        this.commandManager.addOrUpdate(new SetMaskinlagCommand((instance) => {
                            instance.arbetsorderId = x.id;
                            instance.maskinlagId = x.nyttMaskinlagId;
                            instance.startdatum = new Date(this.periodsattPeriod);
                            instance.isSuggestedDate = true;
                        }), c => c.$type === SetMaskinlagCommand.$type && c.arbetsorderId === x.id);
                        break;
                    case IntentType.setMaskinlag:
                        this.commandManager.addOrUpdate(new SetMaskinlagCommand((instance) => {
                            instance.arbetsorderId = x.id;
                            instance.maskinlagId = x.nyttMaskinlagId;
                        }), c => c.$type === SetMaskinlagCommand.$type && c.arbetsorderId === x.id);
                        break;
                }
            });
        const work = from(this.commandService.pushCommands(this.commandManager));
        this.busyService.addWork(work).subscribe(result => {
            this.toastService.add("Ändringar sparade");
            this.modal.close(result);
            this.commandManager.clear();
        });
    }

    private getArbetsorderPlaneradtid(ao: MasshanteringArbetsorder): string {
        let period = this.getArbetsorderPeriod(ao);

        if (ObjectUtils.hasValue(ao.periodsattPeriod)) {
            // Om Mån år (Ex. Jan 2024)
            const date = moment(period).format(DateUtils.dateformat);
            period = date;
        } else {
            // Om åååå-mm-dd - åååå-mm-dd eller åååå-mm-dd
            period = period.substring(0, 10);
        }
        // Returnera max-date om inget finns
        return ObjectUtils.stringHasValue(period) ? period : DateUtils.getMaxDateString();
    }

    private loadMaskinlag(): void {
        this.maskinlagService.getMinaMaskinlag().then(x => {
            const maskinlag = x.filter(y => y.maskinlagstyp !== Maskinlagstyp.Grot);
            const grotMaskinlag = x; // Grotskotning ska tillåta samliga maskinlag
            if (this.data.isMixedMode) {
                this.minaMaskinlag = x;
            } else {
                this.minaMaskinlag = this.data.isOnlyGrotskotning ? grotMaskinlag : maskinlag;
            }

            this.data.masshanteringArbetsorder.forEach(element => {
                if (element.isGrotskotning) {
                    element.maskinlag = grotMaskinlag;
                } else {
                    element.maskinlag = maskinlag;
                }
            });
        });

        this.maskinlagService.getAllMaskinlag().then(x => {
            this.allaMaskinlag = x;
        });
    }

    private sortMasshanteringArbetsorderList(arbetsorderLista: MasshanteringArbetsorder[]): void {
        // Sortera in "låsta" sist i listan, sortera sedan på planerad tid och beteckning
        this.data.masshanteringArbetsorder = sortBy(this.data.masshanteringArbetsorder,
            [
                ao => ao.isLocked,
                ao => this.getArbetsorderPlaneradtid(ao),
                ao => ao.beteckning
            ]);
    }
}
